import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import Button from "components/Button";
import { Board, TitleArea, Title, PageTop } from "components/LayoutParts";
import Loading from "components/Loading";
import UserModal, { UserModalConfig } from "components/UserModal";
import { sortDirection } from "constants/direction";
import { useSwr } from "hooks/useSwr";
import DataTable from "pages/PitPort/User/DataTable";
import ActionArea from "pages/PitPort/User/DataTable/ActionArea";
import { jumpUpToElement } from "utils/animationUtils";
import { formatQueryParams } from "utils/stringUtils";

// パーツ定義
const CreateWrap = styled.div`
  display: flex;
  flex: auto;
  justify-content: flex-end;
`;

// 本体
const User = () => {
  const DISPLAY_FILTER_KEY = "display-user-filter-status";
  const displayFilterStatus = (): PitPortUserDisplayFilterStatus => {
    const storageStatus = localStorage.getItem(DISPLAY_FILTER_KEY);
    if (!storageStatus) return "enabled";
    return JSON.parse(storageStatus);
  };

  const [originList, setOriginList] = useState<PitPortUser[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isModalShow, setIsModalShow] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption<SortByPitPortUser>>({
    sortByColumn: "id",
    direction: sortDirection.desc,
  });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<PitPortUserDisplayFilterStatus>(displayFilterStatus());

  const navigate = useNavigate();
  const navCreateUser = () => navigate("/pit_port/user/create");

  const hideModal = () => setIsModalShow(false);
  const showModal = () => setIsModalShow(true);

  const userModalConfig: UserModalConfig = {
    onClickOutside: hideModal,
    subtitle: "送信に失敗しました",
    main: {
      buttonType: "secondary",
      onClick: hideModal,
      label: "閉じる",
    },
    errorMessage: "時間をおいてもう一度お試しください",
  };

  const { data, error } = useSwr<PitPortUsersResponse>(
    `/user?${formatQueryParams({
      pageIndex: currentPageIndex,
      pageSize: 100,
      status: selectedFilter,
      orderBy: sortOption.sortByColumn,
      orderType: sortOption.direction,
      keyword: searchKeyword,
    })}`
  );

  const fetch = useCallback(async () => {
    jumpUpToElement("page-top");
    if (error) {
      showModal();
      return;
    }
    if (!data) return;
    setTotalPages(data.totalPages);
    setOriginList(data.data);
  }, [data, error]);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  };

  const onFilterButtonClick = (statusforDisplay: PitPortUserDisplayFilterStatus) => {
    setSelectedFilter(statusforDisplay);
    localStorage.setItem(DISPLAY_FILTER_KEY, JSON.stringify(selectedFilter));
  };

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <>
      <Board>
        <PageTop />
        <TitleArea>
          <Title>ユーザ</Title>
          <CreateWrap>
            <Button type="primary" onClick={navCreateUser} label="ユーザを追加" width="160px" />
          </CreateWrap>
        </TitleArea>
        <ActionArea
          searchKeyword={searchKeyword}
          selectedFilter={selectedFilter}
          onSearchChange={onSearchChange}
          onFilterButtonClick={onFilterButtonClick}
        />
        {data ? (
          <DataTable
            userList={originList}
            totalPages={totalPages}
            pageIndex={currentPageIndex}
            sortOption={sortOption}
            setPageIndex={setCurrentPageIndex}
            setSortOption={setSortOption}
            searchKeyword={searchKeyword}
            selectedFilter={selectedFilter}
          />
        ) : (
          <Loading />
        )}
      </Board>
      <UserModal isVisible={isModalShow} config={userModalConfig}></UserModal>
    </>
  );
};

export default User;
